import { Plugin } from '@ckeditor/ckeditor5-core';
import { ClipboardPipeline } from '@ckeditor/ckeditor5-clipboard';
import { UpcastWriter } from '@ckeditor/ckeditor5-engine';

import {
    downcastRecorderAttribute,
    downcastRecorderAlignment,
    downcastRecorderResized,
    downcastRecorderWidth,
    upcastRecorderAlignment,
    upcastRecorderWidth,
    upcastRecorderResized
} from './converters';
import RecorderEditing from './recorderediting';
// import RecorderTypeCommand from './recordertypecommand';
import RecorderUtils from '../recorderutils';
import {
    createRecorderViewElement,
    determineRecorderTypeForInsertionAtSelection,
    getRecorderViewElementMatcher
} from './utils';

export default class RecorderInlineEditing extends Plugin {
    static get requires() {
        return [ RecorderEditing, RecorderUtils, ClipboardPipeline ];
    }

    static get pluginName() {
        return 'RecorderInlineEditing';
    }

    init() {
        const editor = this.editor;
        const schema = editor.model.schema;

        schema.register('recorderInline', {
            isObject: true,
            isInline: true,
            allowWhere: '$text',
            allowAttributes: [ 'src', 'width', 'alignment', 'type', 'controls', 'resized' ]
        });

        schema.addChildCheck((context, childDefinition) => {
            if (context.endsWith('caption') && childDefinition.name === 'recorderInline') {
                return false;
            }
        });

        this._setupConversion();

        if (editor.plugins.has('RecorderBlockEditing')) {
            editor.commands.add(
                'recorderTypeInline',
                new RecorderTypeCommand(this.editor, 'recorderInline')
            );
            this._setupClipboardIntegration();
        }
    }

    _setupConversion() {
        const editor = this.editor;
        const conversion = editor.conversion;
        const recorderUtils = editor.plugins.get('RecorderUtils');

        // Downcast → Data
        conversion.for('dataDowncast').elementToElement({
            model: 'recorderInline',
            view: (modelElement, { writer }) =>
                createRecorderViewElement(writer, modelElement, 'recorderInline')
        });

        // Downcast → Editing
        conversion.for('editingDowncast').elementToElement({
            model: 'recorderInline',
            view: (modelElement, { writer }) =>
                recorderUtils.toRecorderWidget(
                    createRecorderViewElement(writer, modelElement, 'recorderInline'),
                    writer
                )
        });

        // Downcast atributos
        conversion.for('downcast').add(
            downcastRecorderAttribute(recorderUtils, 'recorderInline', 'src')
        );
        conversion.for('downcast').add(
            downcastRecorderWidth('recorderInline')
        );
        conversion.for('downcast').add(
            downcastRecorderAlignment('recorderInline')
        );
        conversion.for('downcast').add(
            downcastRecorderResized('recorderInline')
        );

        // Upcast
        conversion.for('upcast').elementToElement({
            view: getRecorderViewElementMatcher(editor, 'recorderInline'),
            model: (viewRecorder, { writer }) => {
                const container = viewRecorder.parent; // <span class="recorder-inline">
                const attrs = {};

                if (viewRecorder.hasAttribute('src')) {
                    attrs.src = viewRecorder.getAttribute('src');
                    attrs.type = viewRecorder.name; // audio | video
                }
                if (container.getStyle('width')) {
                    attrs.width = container.getStyle('width');
                }
                if (container.hasClass('recorder_resized')) {
                    attrs.resized = true;
                }

                return writer.createElement('recorderInline', attrs);
            }
        });

        conversion.for('upcast').add(upcastRecorderAlignment('recorderInline'));
        // conversion.for('upcast').add(upcastRecorderWidth('recorderInline'));
        conversion.for('upcast').add(upcastRecorderResized('recorderInline'));
    }

    _setupClipboardIntegration() {
        const editor = this.editor;
        const model = editor.model;
        const editingView = editor.editing.view;
        const recorderUtils = editor.plugins.get('RecorderUtils');
        const clipboardPipeline = editor.plugins.get('ClipboardPipeline');

        this.listenTo(clipboardPipeline, 'inputTransformation', (evt, data) => {
            const docFragmentChildren = Array.from(data.content.getChildren());
            let modelRange;

            if (!docFragmentChildren.every(recorderUtils.isBlockRecorderView)) {
                return;
            }

            if (data.targetRanges) {
                modelRange = editor.editing.mapper.toModelRange(data.targetRanges[0]);
            } else {
                modelRange = model.document.selection.getFirstRange();
            }

            const selection = model.createSelection(modelRange);

            if (
                determineRecorderTypeForInsertionAtSelection(model.schema, selection) === 'recorderInline'
            ) {
                const writer = new UpcastWriter(editingView.document);

                const inlineViewRecorders = docFragmentChildren.map(blockViewRecorder => {
                    if (blockViewRecorder.childCount === 1) {
                        Array.from(blockViewRecorder.getAttributes()).forEach(attribute =>
                            writer.setAttribute(
                                ...attribute,
                                recorderUtils.findViewRecorderElement(blockViewRecorder)
                            )
                        );
                        return blockViewRecorder.getChild(0);
                    } else {
                        return blockViewRecorder;
                    }
                });

                data.content = writer.createDocumentFragment(inlineViewRecorders);
            }
        });
    }
}
